import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {Aangifte, BokkzIbDataService, IncomeTaxEntity} from "@snelstart-ventures/fiscaal/domain-logic";
import {debounceTime, distinctUntilChanged, filter, map, Observable, shareReplay, Subscription, take} from "rxjs";
import {FormControl, FormGroup} from "@angular/forms";
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-meer-banen-tegelijk',
  templateUrl: './meer-banen-tegelijk.component.html',
  styleUrls: ['./meer-banen-tegelijk.component.scss'],
  imports: [
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    CommonModule,
    SharedUiAlertModule
  ]
})
export class MeerBanenTegelijkComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public index = 0;
  public isPartner = false;
  public isDefAangifte = {} as boolean;

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public formGroup = new FormGroup({
    dubbelePositie: new FormControl<boolean | null>(false)
  })

  public optionsJaNee: SelectFormControlItem<boolean | undefined>[] = [
    {value: true, label: "Ja"},
    {value: false, label: "Nee"},
  ];

  ngOnInit(): void {
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(entity => {
        if (partner) {
          this.formGroup.patchValue({dubbelePositie: entity.aangifte?.partnerAangifte?.box1?.belastbaarLoon?.dubbelePositie ?? false}, {emitEvent: false});
        } else {
          this.formGroup.patchValue({dubbelePositie: entity.aangifte?.box1?.belastbaarLoon?.dubbelePositie ?? false}, {emitEvent: false});
        }
      });
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    this.changeSubscriptions.add(
      this.formGroup.controls.dubbelePositie.valueChanges.pipe(
        debounceTime(500),
        map(() => this.saveForm()),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        this.saveForm();
        this.dataService.calculateLatestIncomeTaxReturn().subscribe();
      })
    );
  }

  saveForm(): void {
    this.formGroup.updateValueAndValidity({emitEvent: false});

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      const updateBelastbaarLoon = (aangifte: Aangifte) => ({
        ...aangifte,
        box1: {
          ...aangifte.box1,
          belastbaarLoon: {
            ...aangifte.box1?.belastbaarLoon,
            dubbelePositie: this.formGroup.getRawValue().dubbelePositie,
          },
        },
      });

      const updatedAangifte = this.isPartner ? {
        ...currentEntity.aangifte,
        partnerAangifte: updateBelastbaarLoon(currentEntity.aangifte?.partnerAangifte!),
      } : updateBelastbaarLoon(currentEntity.aangifte);

      return {
        ...currentEntity,
        aangifte: updatedAangifte,
      } as IncomeTaxEntity;
    });
  }
}
